'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from '@/components/Image';
import { CoordinatePlaneEntry } from '@/components/whiteboard-v2/CoordinatePlaneEntry';
import { GeometryEntry } from '@/components/whiteboard-v2/GeometryEntry';
import { NumberLineEntry } from '@/components/whiteboard-v2/NumberLineEntry';
import type {
  CoordinatePlanePayload,
  GeometryPayload,
  NumberLinePayload,
} from '@/components/whiteboard-v2/types';
import { generateCoordinatePlanePayload } from '@/lib/coordinatePlaneGenerator';
import { generateGeometryPayload } from '@/lib/geometryGenerator';
import { generateNumberLinePayload } from '@/lib/numberLineGenerator';
import {
  isVisualRenderEnabled,
  type VisualPayload,
  type VisualSpec,
} from '@/lib/visual-diagram';

interface VisualDiagramProps {
  visual?: VisualSpec | null;
  picture?: string | null;
  pictureAlt?: string;
  context?: string;
  className?: string;
}

type CompileStatus = 'idle' | 'loading' | 'error';

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isCoordinatePayload(payload: unknown): payload is CoordinatePlanePayload {
  return isObjectLike(payload)
    && Number.isFinite(payload.xMin)
    && Number.isFinite(payload.xMax)
    && Number.isFinite(payload.yMin)
    && Number.isFinite(payload.yMax);
}

function isGeometryPayload(payload: unknown): payload is GeometryPayload {
  return isObjectLike(payload)
    && Number.isFinite(payload.xMin)
    && Number.isFinite(payload.xMax)
    && Number.isFinite(payload.yMin)
    && Number.isFinite(payload.yMax);
}

function isNumberLinePayload(payload: unknown): payload is NumberLinePayload {
  return isObjectLike(payload)
    && Number.isFinite(payload.min)
    && Number.isFinite(payload.max);
}

function isPayloadCompatible(mode: VisualSpec['mode'], payload: unknown): payload is VisualPayload {
  if (!payload) return false;
  if (mode === 'coordinate_plane') return isCoordinatePayload(payload);
  if (mode === 'geometry') return isGeometryPayload(payload);
  if (mode === 'number_line') return isNumberLinePayload(payload);
  return false;
}

function defaultDescriptionForMode(mode: VisualSpec['mode']): string {
  if (mode === 'geometry') {
    return 'Draw the geometric figure described in the text and label essential points, sides, and angles.';
  }
  if (mode === 'number_line') {
    return 'Draw a number line that shows the key values and interval boundaries from the text.';
  }
  if (mode === 'coordinate_plane') {
    return 'Draw a coordinate-plane graph that represents the relationship described in the text.';
  }
  return '';
}

function isLikelyPlaceholderPayload(mode: VisualSpec['mode'], payload: VisualPayload): boolean {
  if (mode === 'number_line' && isNumberLinePayload(payload)) {
    const pointLabels = (payload.points || []).map((point) => (point.label || '').trim().toLowerCase());
    const intervalLabels = (payload.intervals || []).map((interval) => (interval.label || '').trim().toLowerCase());
    return (
      pointLabels.includes('a')
      && pointLabels.includes('b')
      && intervalLabels.some((label) => label === 'a ≤ x < b' || label === 'a <= x < b')
    );
  }

  if (mode === 'coordinate_plane' && isCoordinatePayload(payload)) {
    const lines = payload.lines || [];
    const curves = payload.curves || [];
    if (curves.length > 0) return false;
    if (lines.length !== 1) return false;
    const line = lines[0];
    const label = (line.label || '').trim().toLowerCase();
    return (
      line.form === 'slope_intercept'
      && Number(line.m) === 1
      && Number(line.b) === 0
      && (!label || label === 'y = x')
    );
  }

  if (mode === 'geometry' && isGeometryPayload(payload)) {
    const hasTrianglePolygon = (payload.polygons || []).some((polygon) => polygon.vertices?.length === 3);
    const hasNamedTrianglePoints = (payload.points || []).map((point) => (point.label || '').trim().toUpperCase());
    const hasOnlyBasicTriangleLabels = hasNamedTrianglePoints.includes('A')
      && hasNamedTrianglePoints.includes('B')
      && hasNamedTrianglePoints.includes('C');
    const hasCircle = (payload.circles || []).length > 0;
    const hasAngles = (payload.angles || []).length > 0;
    return hasTrianglePolygon && hasOnlyBasicTriangleLabels && !hasCircle && !hasAngles;
  }

  return false;
}

function renderBoard(mode: VisualSpec['mode'], payload: VisualPayload) {
  if (mode === 'coordinate_plane' && isCoordinatePayload(payload)) {
    return <CoordinatePlaneEntry payload={payload} />;
  }
  if (mode === 'geometry' && isGeometryPayload(payload)) {
    return <GeometryEntry payload={payload} />;
  }
  if (mode === 'number_line' && isNumberLinePayload(payload)) {
    return <NumberLineEntry payload={payload} />;
  }
  return null;
}

function renderFallbackImage(picture?: string | null, pictureAlt?: string) {
  if (!picture) return null;

  return (
    <div className="w-full flex items-center justify-center mt-2">
      <Image
        src={picture}
        alt={pictureAlt || 'diagram'}
        width={420}
        height={280}
        optimizedWidth={640}
        className="rounded-lg object-contain"
      />
    </div>
  );
}

export default function VisualDiagram({ visual, picture, pictureAlt, context, className }: VisualDiagramProps) {
  const renderEnabled = isVisualRenderEnabled();
  const hasVisual = !!visual;
  const mode = visual?.mode ?? 'none';
  const description = visual?.description?.trim() || '';
  const effectiveDescription = description || defaultDescriptionForMode(mode);
  const normalizedContext = typeof context === 'string' && context.trim().length > 0
    ? context.trim()
    : undefined;

  const initialPayload = useMemo(() => {
    if (!visual?.payload) return null;
    if (!isPayloadCompatible(mode, visual.payload)) return null;
    if (normalizedContext && isLikelyPlaceholderPayload(mode, visual.payload)) {
      return null;
    }
    return visual.payload;
  }, [mode, normalizedContext, visual?.payload]);

  const [payload, setPayload] = useState<VisualPayload | null>(initialPayload);
  const [status, setStatus] = useState<CompileStatus>('idle');

  useEffect(() => {
    setPayload(initialPayload);
    setStatus('idle');
  }, [initialPayload, mode, effectiveDescription]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!renderEnabled) return;
      if (!hasVisual) return;
      if (mode === 'none') return;
      if (payload) return;
      if (!effectiveDescription) return;

      setStatus('loading');

      try {
        let generated: VisualPayload;

        if (mode === 'coordinate_plane') {
          generated = await generateCoordinatePlanePayload(effectiveDescription, normalizedContext);
        } else if (mode === 'geometry') {
          generated = await generateGeometryPayload(effectiveDescription, normalizedContext);
        } else {
          generated = await generateNumberLinePayload(effectiveDescription, normalizedContext);
        }

        if (!cancelled) {
          setPayload(generated);
          setStatus('idle');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [effectiveDescription, hasVisual, mode, normalizedContext, payload, renderEnabled]);

  if (!renderEnabled) {
    return renderFallbackImage(picture, pictureAlt);
  }

  if (!visual || mode === 'none') {
    return renderFallbackImage(picture, pictureAlt);
  }

  const diagram = payload ? renderBoard(mode, payload) : null;

  if (diagram) {
    return (
      <div className={className || 'w-full mt-3'}>
        <div className="w-full rounded-xl border border-[#e9debf] bg-[#fffaf0] p-2 md:p-3">
          {diagram}
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className={className || 'w-full mt-3'}>
        <div className="w-full rounded-xl border border-dashed border-[#d8cfb4] bg-[#f8f3e7] px-4 py-3 text-center text-sm text-[#8b735f]">
          Generating diagram...
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const fallback = renderFallbackImage(picture, pictureAlt);
    if (fallback) return fallback;

    if (visual.required) {
      return (
        <div className={className || 'w-full mt-3'}>
          <div className="w-full rounded-xl border border-dashed border-[#f3c3c3] bg-[#fff6f6] px-4 py-3 text-center text-sm text-[#9e5454]">
            Diagram is temporarily unavailable.
          </div>
        </div>
      );
    }
  }

  return renderFallbackImage(picture, pictureAlt);
}
